import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Company } from "@/data/companies/types";

interface UserCompanyContextType {
  company: Company | null;
  companyId: string | null;
  isLoading: boolean;
  error: Error | null;
  isCompanyManager: boolean;
  isCompanyAdmin: boolean;
  refetch: () => Promise<void>;
  getCompanyDashboardUrl: (tab?: string, section?: string) => string | null;
}

const UserCompanyContext = createContext<UserCompanyContextType | undefined>(undefined);

export function UserCompanyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUserCompany = useCallback(async () => {
    if (!user) {
      setCompany(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // First, get the company IDs that the user manages
      const { data: companyManagers, error: managersError } = await supabase
        .from('company_managers')
        .select('company_id, role')
        .eq('user_id', user.id)
        .limit(1);

      if (managersError) throw managersError;

      // If user doesn't manage any companies
      if (!companyManagers || companyManagers.length === 0) {
        setCompany(null);
        setIsLoading(false);
        return;
      }

      // Get the company details
      const { data: companyData, error: companiesError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyManagers[0].company_id)
        .limit(1)
        .single();

      if (companiesError) throw companiesError;

      // Map the database fields to our Company type
      const mappedCompany: Company = {
        id: companyData.id,
        name: companyData.name,
        logo: companyData.logo_url,
        logo_url: companyData.logo_url,
        description: companyData.description,
        industry: companyData.industry || "",
        location: companyData.location || "",
        size: companyData.size || "",
        website: companyData.website || "",
        created_at: companyData.created_at,
        updated_at: companyData.updated_at,
        role: (companyManagers[0].role as 'admin' | 'manager' | 'user') || 'user',
        avgRating: 0,
        reviewCount: 0,
        salaryCount: 0,
      };

      setCompany(mappedCompany);
    } catch (err: any) {
      console.error("Error fetching user company:", err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUserCompany();
  }, [fetchUserCompany]);

  // Generate company dashboard URL with optional tab and section
  const getCompanyDashboardUrl = useCallback((tab?: string, section?: string): string | null => {
    if (!company?.id) return null;
    
    let url = `/company-dashboard/${company.id}`;
    const params = new URLSearchParams();
    
    if (tab) params.set('tab', tab);
    if (section) params.set('section', section);
    
    const queryString = params.toString();
    return queryString ? `${url}?${queryString}` : url;
  }, [company?.id]);

  const value: UserCompanyContextType = {
    company,
    companyId: company?.id || null,
    isLoading,
    error,
    isCompanyManager: !!company,
    isCompanyAdmin: company?.role === 'admin',
    refetch: fetchUserCompany,
    getCompanyDashboardUrl,
  };

  return (
    <UserCompanyContext.Provider value={value}>
      {children}
    </UserCompanyContext.Provider>
  );
}

export function useUserCompanyContext() {
  const context = useContext(UserCompanyContext);
  if (context === undefined) {
    throw new Error('useUserCompanyContext must be used within a UserCompanyProvider');
  }
  return context;
}

// Standalone utility for generating company dashboard URLs (for use outside React)
export function getCompanyDashboardUrl(companyId: string, tab?: string, section?: string): string {
  let url = `/company-dashboard/${companyId}`;
  const params = new URLSearchParams();
  
  if (tab) params.set('tab', tab);
  if (section) params.set('section', section);
  
  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}
